<script setup lang="ts">
/*
 * Native checkbox with branded styling. Optional inline label for the common
 * "checkbox + label" pairing. For grouped checkboxes, render multiple of
 * these inside a FormField (or a manual <fieldset>).
 *
 * Non-prop attrs (aria-label, data-testid, etc.) fall through to the <input>
 * so callers can set accessible labels without a visible text label.
 */
import { useId } from 'vue';

defineOptions({ inheritAttrs: false });

withDefaults(
  defineProps<{
    modelValue: boolean;
    label?: string;
    disabled?: boolean;
    indeterminate?: boolean;
    id?: string;
    name?: string;
  }>(),
  {},
);

defineEmits<{ 'update:modelValue': [v: boolean] }>();
const fallbackId = useId();
</script>

<template>
  <label
    :for="id ?? fallbackId"
    :class="[
      'inline-flex cursor-pointer select-none items-center gap-2 text-md text-app-fg',
      disabled ? 'cursor-not-allowed opacity-50' : '',
    ]"
  >
    <input
      v-bind="$attrs"
      :id="id ?? fallbackId"
      type="checkbox"
      :checked="modelValue"
      :disabled="disabled"
      :name="name"
      :indeterminate="indeterminate"
      class="h-4 w-4 cursor-pointer rounded-sm border-app-border accent-primary text-primary focus:ring-3 focus:ring-primary/20"
      @change="$emit('update:modelValue', ($event.target as HTMLInputElement).checked)"
    />
    <span v-if="label || $slots.default">
      <slot>{{ label }}</slot>
    </span>
  </label>
</template>
